// frontend/src/state/analysis/analysisReducers.js
// Purpose: Synchronous (non-thunk) Redux reducers for the analysis slice.
// Desired outcome: Manages direct state updates for typical user actions within the analysis workflow, ensuring efficient state handling without triggering redundant API calls.


const analysisReducers = {
  // ✅ Sets the current step in the workflow (e.g., "fileSelection", "columnMapping", etc.)
  setStep: (state, action) => {
    state.step = action.payload;
  },

  // ✅ Updates the "progress" data (like loading status, percent complete, etc.)
  updateProgress: (state, action) => {
    state.progress = {
      ...state.progress,
      ...action.payload
    };
  },

  // ✅ Clears all analysis-related data, **but does not reset `partMatches` unless explicitly required**.
  clearAnalysis: (state, action) => {
    const { resetMatches = false } = action.payload || {};

    state.step = 'fileSelection';
    state.currentFile = null;
    state.extractedColumns = [];
    state.columnMappings = {
      suggested: {},
      confirmed: {}
    };

    if (resetMatches) {
      state.partMatches = []; // ✅ Only reset if explicitly requested
    }

    state.confidenceThreshold = 70; // Reset to default confidence threshold
    state.contextualizedParts = [];
    state.calculations = {
      data: null,
      metadata: null,
      loading: false,
      error: null,
      scenario_A: { // Maintain scenario A state
        data: null,
        loading: false,
        error: null,
      },
      scenario_B: { // Maintain scenario B state
        data: null,
        loading: false,
        error: null,
      },
      scenario_C: { // Add scenario C state
        data: null,
        loading: false,
        error: null,
      }
    }; // ✅ Reset calculations state
    state.reportData = {};
    state.progress = {
      currentStep: "",
      percentComplete: 0,
      status: "idle"
    };
    state.error = null;
  },

  // ✅ Manually sets an error in the state without affecting partMatches
  setError: (state, action) => {
    state.error = action.payload;

    // ✅ Ensure setting an error does NOT reset or affect core state
    if (!state.progress) {
      state.progress = {};
    }
    state.progress.status = 'failed';
  },

  // ✅ Clears any stored error
  clearError: (state) => {
    state.error = null;
  },

  // ✅ Updates mappings for file columns, typically used after user confirms them
  updateColumnMappings: (state, action) => {
    state.columnMappings = {
      ...state.columnMappings,
      ...action.payload
    };
  },
  
  // ✅ Updates part matching confidence threshold - RENAMED from setConfidenceThreshold
  updateConfidenceThreshold: (state, action) => {
    state.confidenceThreshold = action.payload;
  },
  
  // ✅ Updates a single part match (typically for manual override)
  updatePartMatch: (state, action) => {
    const { partID, matchedCategory, status } = action.payload;
    const index = state.partMatches.findIndex(p => p.partID === partID);
    
    if (index !== -1) {
      state.partMatches[index].matchedCategory = matchedCategory;
      state.partMatches[index].status = status || 'confirmed';
    }
  },

  // ✅ Updates a single contextualized part
  updateContextualizedPart: (state, action) => {
    const { partID, mappedColumns } = action.payload;
    const index = state.contextualizedParts.findIndex(p => p.partID === partID);
    
    if (index !== -1) {
      state.contextualizedParts[index].mappedColumns = {
        ...state.contextualizedParts[index].mappedColumns,
        ...mappedColumns
      };
    }
  },

  // ✅ Pushes a new report into the "reports" array
  addReport: (state, action) => {
    state.reports.push(action.payload);
  },

  // ✅ Updates calculation results in the state
  setCalculations: (state, action) => {
    state.calculations.data = action.payload.data;
    state.calculations.metadata = action.payload.metadata; // ✅ Stores calculation metadata (units, confidence scores)
    state.calculations.loading = false;
    state.calculations.error = null;
  },

  // ✅ Sets the loading state for calculations
  setCalculationsLoading: (state) => {
    state.calculations.loading = true;
    state.calculations.error = null;
  },

  // ✅ Stores an error if calculations fail
  setCalculationsError: (state, action) => {
    state.calculations.loading = false;
    state.calculations.error = action.payload;
    state.calculations.metadata = null; // ✅ Clear metadata on error for improved error handling
  },

  // Scenario-specific calculation reducers
  setCalculationsScenarioA: (state, action) => {
    state.calculations.scenario_A.data = action.payload;
    state.calculations.scenario_A.loading = false;
    state.calculations.scenario_A.error = null;
  },
  setCalculationsLoadingScenarioA: (state) => {
    state.calculations.scenario_A.loading = true;
    state.calculations.scenario_A.error = null;
  },
  setCalculationsErrorScenarioA: (state, action) => {
    state.calculations.scenario_A.loading = false;
    state.calculations.scenario_A.error = action.payload;
  },
  
  setCalculationsScenarioB: (state, action) => {
    state.calculations.scenario_B.data = action.payload;
    state.calculations.scenario_B.loading = false;
    state.calculations.scenario_B.error = null;
  },
  setCalculationsLoadingScenarioB: (state) => {
    state.calculations.scenario_B.loading = true;
    state.calculations.scenario_B.error = null;
  },
  setCalculationsErrorScenarioB: (state, action) => {
    state.calculations.scenario_B.loading = false;
    state.calculations.scenario_B.error = action.payload;
  },
  
  // Scenario C calculation reducers
  setCalculationsScenarioC: (state, action) => {
    state.calculations.scenario_C.data = action.payload;
    state.calculations.scenario_C.loading = false;
    state.calculations.scenario_C.error = null;
  },
  setCalculationsLoadingScenarioC: (state) => {
    state.calculations.scenario_C.loading = true;
    state.calculations.scenario_C.error = null;
  },
  setCalculationsErrorScenarioC: (state, action) => {
    state.calculations.scenario_C.loading = false;
    state.calculations.scenario_C.error = action.payload;
  },

  // ✅ Update scenario type for value savings
  updateValueSavingsScenarioType: (state, action) => {
    state.valueSavings.scenarioType = action.payload;
  },

  // ✅ Update assumptions for value savings
  updateValueSavingsAssumptions: (state, action) => {
    state.valueSavings.assumptions = {
      ...state.valueSavings.assumptions,
      ...action.payload
    };
  },

  // ✅ Reset value savings analysis
  resetValueSavings: (state) => {
    state.valueSavings = {
      data: null,
      loading: false,
      error: null,
      scenarioType: 'brownfield',
      assumptions: {
        annualHoldingCostPct: 0.20,
        obsolescencePct_current: 0.10,
        obsolescencePct_onDemand: 0.01,
        warehouseSetupCost: 100000,
        annualWarehouseOperationalCost: 50000,
        responsivenessRevenueIncreasePct: 0.02
      }
    };
  },
  
  // New functions for Module 10: Report Generation
  
  // Updates the report configuration (which sections to include in the PDF)
  updateReportConfig: (state, action) => {
    state.reportData.reportConfig = {
      ...state.reportData.reportConfig,
      ...action.payload
    };
  },
  
  // Updates the report name
  updateReportName: (state, action) => {
    state.reportData.name = action.payload;
  },
  
  // Sets the report generation status (loading state)
  setReportGenerating: (state, action) => {
    state.reportData.generating = action.payload;
  },
  
  // Sets an error that occurred during report generation
  setReportError: (state, action) => {
    state.reportData.error = action.payload;
  },
  
  // Updates the download URLs for report exports
  updateReportDownloadURLs: (state, action) => {
    state.reportData.downloadURLs = {
      ...state.reportData.downloadURLs,
      ...action.payload
    };
  },
  
  // Sets the generated timestamp for a report
  setReportGeneratedAt: (state, action) => {
    state.reportData.generatedAt = action.payload;
  },
  
  // Sets the executive summary data
  setExecutiveSummary: (state, action) => {
    state.reportData.executiveSummary = action.payload;
  },
  
  // Updates the rankings data (top parts, categories)
  updateReportRankings: (state, action) => {
    state.reportData.rankings = {
      ...state.reportData.rankings,
      ...action.payload
    };
  },
  
  // Updates the category analysis data
  updateCategoryAnalysis: (state, action) => {
    state.reportData.categoryAnalysis = {
      ...state.reportData.categoryAnalysis,
      ...action.payload
    };
  }
};

export default analysisReducers;